import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as dms from '../../dms'
import type * as mosaicModels from '../models'

import type { Feature } from './feature'
import * as models from './models'

export class Selectors {
  /** TODO:  */
  public readonly charts
  /** TODO:  */
  public readonly getChart
  /** TODO:  */
  public readonly getChartData
  /** TO DO:  */
  public readonly getChartMetadata
  /** TODO:  */
  public readonly getNodes
  /** TODO:  */
  public readonly getNode
  /** TODO:  */
  public readonly getEdges
  /** TODO:  */
  public readonly getEditingNode
  /** TODO:  */
  public readonly getSkills
  /** TODO:  */
  public readonly getProjectEngagements
  /** TODO:  */
  public readonly getAvailability
  /** TODO:  */
  public readonly canUndo
  /** TODO:  */
  public readonly canRedo

  /** TODO:  */
  public readonly hasProposal
  /** TODO:  */
  public readonly hasProposedChange
  /** TODO:  */
  public readonly getApplicableModelId
  /** TODO:  */
  public readonly isMutating
  /** TODO: */
  public readonly error
  /** TODO:  */
  public readonly isUploadingResumeFile

  constructor(feature: Feature, schemaFeature: dms.schema.Feature) {
    this.charts = feature.state.project(state => state.charts)

    this.getChart = feature.createSelectorMethod(
      ({ chartId }: models.Chart.Identifier) =>
        this.charts.project(charts => charts[chartId]),
    )
    this.getChartData = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChart(identifier).project(
          chart =>
            chart &&
            ({
              // NOTE: the entire purpose of this is to focus on just the core data
              // for more efficient change detection
              edges: chart.edges,
              nodes: chart.nodes,
            } satisfies models.Data),
        ),
    )
    this.getChartMetadata = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChart(identifier).project(chart => chart?.metadata),
    )

    this.getNodes = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChartData(identifier).project(data => data?.nodes ?? []),
    )
    this.getNode = feature.createSelectorMethod(
      (
        params: models.Chart.Identifier & {
          nodeId: ents.core.display.tree.Node.Id
        },
      ) =>
        this.getNodes(params).project(nodes =>
          nodes.find(
            node => ents.core.display.tree.Node.Id.get(node) === params.nodeId,
          ),
        ),
    )
    this.getEdges = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChartData(identifier).project(data => data?.edges ?? []),
    )
    this.getEditingNode = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChart(identifier).project(chart => {
          const nodeId = chart?.currentlyEditingNodeId
          return nodeId ? chart?.edits[nodeId] : undefined
        }),
    )
    const getPeopleInsights = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChart(identifier).project(chart =>
          chart?.currentlyEditingNodeId
            ? chart.peopleInsightsLookup[chart.currentlyEditingNodeId]
            : undefined,
        ),
    )
    this.getSkills = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        getPeopleInsights(identifier).project(
          peopleInsights => peopleInsights?.skills ?? [],
        ),
    )
    this.getProjectEngagements = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        getPeopleInsights(identifier).project(
          peopleInsights => peopleInsights?.projectEngagements ?? [],
        ),
    )
    this.getAvailability = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        getPeopleInsights(identifier).project(
          peopleInsights => peopleInsights?.availability,
        ),
    )

    this.canUndo = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChart(identifier).project(
          chart => !!chart && chart.history.length - chart.undoCount > 0,
        ),
    )
    this.canRedo = feature.createSelectorMethod(
      (identifier: models.Chart.Identifier) =>
        this.getChart(identifier).project(
          chart => !!chart && chart.undoCount > 0,
        ),
    )

    this.hasProposal = feature.createSelectorMethod(
      ({ widgetRefId }: { widgetRefId: mosaicModels.widget.Ref.Id }) =>
        this.charts.project(
          charts =>
            !!charts[models.Chart.Id.from({ entry: 'proposed', widgetRefId })],
        ),
    )

    const getChangedNodeIds = feature.createSelectorMethod(
      ({ chartId }: models.Chart.Identifier) => {
        if (models.Chart.Entry.get(chartId) !== 'proposed') {
          return store.selectors.Selector.always(
            new Set<ents.core.display.tree.Node.Id>(),
          )
        }

        const [widgetRefId] = models.Chart.Id.getParts(chartId)
        const currentChartId = models.Chart.Id.from(widgetRefId)

        return this.getChart({ chartId }).project(
          this.getChartData({ chartId: currentChartId }).asSelector,
          (proposedChart, currentChart) =>
            _getChangedNodeIds({
              currentChart,
              proposedChart,
            }),
        )
      },
    )

    this.hasProposedChange = feature.createSelectorMethod(
      ({
        nodeId,
        chartId,
      }: {
        nodeId: ents.core.display.tree.Node.Id
      } & models.Chart.Identifier) =>
        getChangedNodeIds({ chartId }).project(changedNodeIds =>
          changedNodeIds.has(nodeId),
        ),
    )

    this.getApplicableModelId = feature.createSelectorMethod(
      ({
        chartId,
        parentId,
      }: models.Chart.Identifier & {
        parentId?: ents.core.display.tree.Node.Id
      }) =>
        this.getChartMetadata({ chartId }).project(
          parentId
            ? this.getNode({ chartId, nodeId: parentId }).project(
                schemaFeature.selectors.schema.asSelector,
                (node, schema) => {
                  if (!node) {
                    return undefined
                  }
                  return { node, schema }
                },
              ).asSelector
            : store.selectors.Selector.always(undefined),
          !parentId
            ? this.getNodes({ chartId }).asSelector
            : store.selectors.Selector.always([]),
          (metadata, schemaAndNode, nodes) => {
            if (metadata?.defaultModelId) {
              return metadata.defaultModelId
            }
            if (schemaAndNode) {
              const { node, schema } = schemaAndNode

              // TODO: what to do for snapshot modelId?
              if (ents.core.dms.models.Entity.Id.isId(node.modelId)) {
                return !schema.isLeaf(node.modelId)
                  ? schema.getChildEntityOf(node.modelId).id
                  : node.modelId // if leaf entity, I'll use this node modelId
              }

              return undefined
            } else {
              // get the one that has most nodes
              return Object.entries(
                nodes.reduce<
                  Partial<
                    Record<
                      ents.core.dms.models.ForRecord.Id,
                      ents.core.display.tree.Node[]
                    >
                  >
                >((map, n) => {
                  ;(map[n.modelId] ??= []).push(n)
                  return map
                }, {}),
              ).reduce<{
                maxCount: number
                modelId: ents.core.dms.models.ForRecord.Id | undefined
              }>(
                ({ maxCount, modelId }, [key, value]) =>
                  value && value.length > maxCount
                    ? {
                        maxCount: value.length,
                        modelId: key as ents.core.dms.models.ForRecord.Id,
                      }
                    : { maxCount, modelId },
                { maxCount: 0, modelId: undefined },
              ).modelId
            }
          },
        ),
    )

    this.isMutating = feature.createSelectorMethod(
      (params: models.Chart.Identifier) =>
        this.getChart(params).project(
          chart => !!chart && chart.isMutating === true,
        ),
    )

    this.error = feature.createSelectorMethod(
      (params: models.Chart.Identifier) =>
        this.getChart(params).project(chart =>
          !!chart && chart.error && chart.error instanceof Error
            ? chart.error.message
            : chart?.error,
        ),
    )

    this.isUploadingResumeFile = feature.state.project(
      state => state.isUploadingResumeFile,
    )
  }
}

/**
 * Returns the committed proposal node ids that still differ from the current
 * chart after accounting for undo/redo state.
 */
function _getChangedNodeIds({
  currentChart,
  proposedChart,
}: {
  currentChart?: models.Data
  proposedChart?: models.Chart
}): ReadonlySet<ents.core.display.tree.Node.Id> {
  if (!currentChart || !proposedChart) {
    return new Set<ents.core.display.tree.Node.Id>()
  }

  const changedNodeIds = new Set<ents.core.display.tree.Node.Id>()
  const touchedNodeIds = _getTouchedNodeIds(proposedChart)

  if (touchedNodeIds.size === 0) {
    return changedNodeIds
  }

  const currentNodesById = _getNodeByIdLookup(currentChart.nodes)
  const currentRelationshipsByChild = _getRelationshipsByChildLookup(
    currentChart.edges,
  )
  const proposedRelationshipsByChild = _getRelationshipsByChildLookup(
    proposedChart.edges,
  )

  for (const nodeId of touchedNodeIds) {
    const proposedNode = proposedChart.nodes.find(
      node => ents.core.display.tree.Node.Id.get(node) === nodeId,
    )

    if (!proposedNode) {
      continue
    }

    const currentNode = currentNodesById.get(nodeId)

    if (!currentNode || !isDeepEqual(currentNode, proposedNode)) {
      changedNodeIds.add(nodeId)
      continue
    }

    if (
      !_areEqualRelationshipSets(
        currentRelationshipsByChild.get(nodeId),
        proposedRelationshipsByChild.get(nodeId),
      )
    ) {
      changedNodeIds.add(nodeId)
    }
  }

  return changedNodeIds
}

/**
 * Collects node ids touched by active proposal history entries.
 */
function _getTouchedNodeIds(
  proposedChart: Pick<models.Chart, 'history' | 'undoCount'>,
): ReadonlySet<ents.core.display.tree.Node.Id> {
  const touchedNodeIds = new Set<ents.core.display.tree.Node.Id>()
  const activeHistory = proposedChart.history.slice(
    0,
    proposedChart.history.length - proposedChart.undoCount,
  )

  for (const change of activeHistory) {
    for (const node of change.nodes.added) {
      touchedNodeIds.add(ents.core.display.tree.Node.Id.get(node))
    }

    for (const node of change.nodes.edited) {
      touchedNodeIds.add(ents.core.display.tree.Node.Id.get(node.to))
    }

    for (const edge of [...change.edges.added, ...change.edges.deleted]) {
      touchedNodeIds.add(edge.childId)
    }
  }

  return touchedNodeIds
}

/**
 * Indexes nodes by id for efficient comparison during proposal diffing.
 */
function _getNodeByIdLookup(
  nodes: readonly ents.core.display.tree.Node[],
): ReadonlyMap<ents.core.display.tree.Node.Id, ents.core.display.tree.Node> {
  const lookup = new Map<
    ents.core.display.tree.Node.Id,
    ents.core.display.tree.Node
  >()

  for (const node of nodes) {
    lookup.set(ents.core.display.tree.Node.Id.get(node), node)
  }

  return lookup
}

/**
 * Builds the typed relationship lookup used for proposal-diff indicators.
 */
function _getRelationshipsByChildLookup(
  edges: readonly models.Edge[],
): ReadonlyMap<ents.core.display.tree.Node.Id, ReadonlySet<string>> {
  const lookup = new Map<ents.core.display.tree.Node.Id, Set<string>>()

  for (const edge of edges) {
    const relationships = lookup.get(edge.childId) ?? new Set<string>()
    relationships.add(
      `${edge.leaderType ?? ents.extra.common.LeaderType.Manager}::${
        edge.parentId
      }`,
    )

    if (!lookup.has(edge.childId)) {
      lookup.set(edge.childId, relationships)
    }
  }

  return lookup
}

/**
 * Compares typed org-chart relationship sets for selector change detection.
 */
function _areEqualRelationshipSets(
  first: ReadonlySet<string> | undefined,
  second: ReadonlySet<string> | undefined,
): boolean {
  if ((first?.size ?? 0) !== (second?.size ?? 0)) {
    return false
  }

  if (!first || first.size === 0) {
    return true
  }

  for (const relationship of first) {
    if (!second?.has(relationship)) {
      return false
    }
  }

  return true
}
